<template>

  <div>
    <Modal
      :title="isNew ? $t('titles.create_organization') : $t('titles.edit_organization')"
      :submit="submit"
      :submitting="submitting"
    >

      <o-text-field
        :label="$t('titles.organization_name')"
        v-model="v$.name.$model"
        :validator="v$.name"
        required
      />

      <o-text-field
        :label="$t('titles.contact_name')"
        v-model="v$.contactName.$model"
        :validator="v$.contactName"
        required
      />

      <o-text-field
        :label="$t('titles.contact_email')"
        v-model="v$.contactEmail.$model"
        :validator="v$.contactEmail"
        required
      />

      <o-text-field
        :label="$t('titles.contact_phone')"
        v-model="v$.contactPhone.$model"
        :validator="v$.contactPhone"
        required
      />

      <o-switch
        :label="$t('misc.live')"
        v-model="formState.live"
      />

    </Modal>
  </div>

</template>

<script setup lang="ts">

import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Organization from '../../../models/Organization'
import type { CreateOrganizationRequest } from '../../../models/Organization'

// validation
import useVuelidate from '@vuelidate/core'
import { required, email, minLength } from '../../../utils/i18n-validators'

// misc
import NotificationService from '../../../services/NotificationService'
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'

const props = defineProps({
  
  organization: {
    type: Organization,
    required: true,
  },

})

const { t } = useI18n()
const isNew = !props.organization.ID

/**
 * Form state and rules
 */

// TODO: how can I resolve this at open time, not at init time?
const formState = reactive({
  name: props.organization.name,
  contactName: props.organization.contactName,
  contactEmail: props.organization.contactEmail,
  contactPhone: props.organization.contactPhone,
  live: props.organization.live,
})

const formRules = {
  name: {
    required,
    minLength: minLength(3),
  },
  contactName: {
    required,
    minLength: minLength(3),
  },
  contactEmail: {
    required,
    email,
  },
  contactPhone: {
    required,
  },
}

const v$ = useVuelidate(formRules, formState)

/**
 * Submission
 */

const submitting = ref<boolean>(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitting.value = true

  let promise

  const payload: CreateOrganizationRequest = {
    name: formState.name,
    contactName: formState.contactName,
    contactEmail: formState.contactEmail,
    contactPhone: formState.contactPhone,
    live: formState.live,
  }

  if (isNew) {
    promise = Organization.saveNew(payload)
  } else {
    promise = Organization.saveExisting(props.organization.ID, payload)
  }

  promise
    .then(() => {
      submitting.value = true
      const msg = isNew ? t('alerts.organization_created') : t('alerts.organization_updated')
      NotificationService.success(msg)
      closeDialog(null)
    })
    .catch((error) => {
      console.error(error)
      const msg = isNew ? t('alerts.couldnt_create_organization') : t('alerts.couldnt_update_organization')
      NotificationService.error(msg)
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
